import { DefaultButton, IButtonStyles, IContextualMenuProps, Modal, PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { format } from "date-fns";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { resourcesContext } from "../../providers/resources";
import { DraftStatus, shiftsContext } from "../../providers/shifts";
import { useRsoConfiguration } from "../../providers/rsoConfigurationContext";
import { AIEmpowerPayload, buildAIEmpowerPayload } from "../../helpers/aiEmpower/AIEmpowerPayloadBuilder";
import { streamChatGPTRequest } from "../../helpers/aiEmpower/ChatGPTOptimizer";
import { closeToast, toast } from "../../helpers/notifications";
import { buildCrmUrl } from "../../helpers/crmUrlBuilder";

interface IAiAllocation {
    jobId: string;
    resourceId: string;
    start: string;
    end: string;
    reason: string;
    matchPercent: number;
}

interface ITimeRange {
    startMs: number;
    endMs: number;
}

interface ISmartSuggestion {
    assignments: IAiAllocation[];
    unassigned: string[];
}

type TSmartDecision = 'pending' | 'accepted' | 'rejected' | 'unmatched';

interface ISmartMatchItem {
    jobId: string;
    title: string;
    start: string | null;
    end: string | null;
    durationLabel: string;
    suggestedResourceId?: string;
    suggestedResourceName?: string;
    suggestedResourceDetails?: ISmartResourceDetails;
    reason?: string;
    matchPercent?: number;
    status: TSmartDecision;
    updatedAt?: string;
}

interface ISmartMatchBatch {
    id: string;
    createdAt: string;
    planningWindow: {
        start: string;
        end: string;
    };
    items: ISmartMatchItem[];
}

interface ISmartMatchHistoryEntry {
    id: string;
    batchId: string;
    timestamp: string;
    action: 'accepted' | 'rejected';
    jobId: string;
    jobTitle: string;
    shiftStart?: string | null;
    shiftEnd?: string | null;
    resourceId?: string;
    resourceName?: string;
    matchPercent?: number;
    reason?: string;
}

interface ISmartResourceSkill {
    label: string;
    expiryDate?: string;
}

interface ISmartResourceDetails {
    skills: ISmartResourceSkill[];
    categories: string[];
    siteLocations: string[];
    minimumWorkHoursPerPayroll?: number | null;
    maximumWorkHourPerPayroll?: number | null;
    maxHoursPerShift?: number | null;
    personalNote?: string | null;
}

interface ISmartResourceLike {
    id: string;
    minimumWorkHoursPerPayroll?: number;
    maximumWorkHourPerPayroll?: number;
    maxHoursPerShift?: number;
    personalNote?: string;
    characteristics?: Array<{
        name?: string;
        ratingName?: string;
        expiryDate?: Date | string;
    }>;
    categories?: Array<{ name?: string }>;
    siteLocation?: Array<{ illumina_name?: string }>;
}

type TBiddingAssignmentsView = 'open' | 'past';

interface IBiddingInviteeItem {
    resourceId: string;
    resourceName: string;
    status: string;
    statusLabel: string;
    isActive: boolean;
    invitationSeenAt?: string | null;
    invitationOpenedAt?: string | null;
    outcomeLabel?: string | null;
}

interface IBiddingAssignmentItem {
    jobId: string;
    title: string;
    start: string | null;
    end: string | null;
    autoAssignAt: string | null;
    selectedResourceId?: string;
    selectedResourceName?: string;
    invitationResourceCount: number;
    activeInvitationCount: number;
    hasSelectedResource: boolean;
    selectedFromInvitedPool: boolean;
    isPast: boolean;
    invitees: IBiddingInviteeItem[];
}

const SMART_MATCH_HISTORY_KEY = "rb_smart_match_history";
const SMART_MATCH_HISTORY_MAX = 400;
const BIDDING_UNASSIGNED_SHIFT_IDS_STORAGE_KEY = 'rb_unassigned_bidding_shift_ids_v1';

type BiddingUnassignedMarker = {
    originalResourceId: string;
    startedAt: string;
};

const sanitizeBiddingMarkerPayload = (value: unknown, fallbackStartedAt?: string): BiddingUnassignedMarker => {
    const fallback = String(fallbackStartedAt ?? new Date().toISOString()).trim();
    const markerStartedAt = String((value as any)?.startedAt ?? '').trim();
    return {
        originalResourceId: String((value as any)?.originalResourceId ?? '').trim(),
        startedAt: Number.isNaN(Date.parse(markerStartedAt)) ? fallback : markerStartedAt
    };
};

const getPersistedBiddingUnassignedShiftMarkers = (): { [shiftId: string]: BiddingUnassignedMarker } => {
    if (typeof window === 'undefined') return {};
    try {
        const raw = window.localStorage?.getItem(BIDDING_UNASSIGNED_SHIFT_IDS_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            const legacy: { [shiftId: string]: BiddingUnassignedMarker } = {};
            parsed.forEach((value) => {
                const shiftId = String(value ?? '').trim();
                if (!shiftId) return;
                legacy[shiftId] = {
                    originalResourceId: '',
                    startedAt: new Date().toISOString()
                };
            });
            return legacy;
        }
        if (parsed && typeof parsed === 'object') {
            const normalized: { [shiftId: string]: BiddingUnassignedMarker } = {};
            Object.entries(parsed).forEach(([shiftId, marker]) => {
                const normalizedShiftId = String(shiftId ?? '').trim();
                if (!normalizedShiftId) return;
                if (typeof marker === 'object' && marker !== null) {
                    normalized[normalizedShiftId] = sanitizeBiddingMarkerPayload(marker, new Date().toISOString());
                    return;
                }
                normalized[normalizedShiftId] = {
                    originalResourceId: '',
                    startedAt: new Date().toISOString()
                };
            });
            return normalized;
        }
        return {};
    } catch {
        return {};
    }
};

const getPersistedBiddingUnassignedShiftMarker = (shiftId: string): BiddingUnassignedMarker | null => {
    const normalizedShiftId = String(shiftId ?? '').trim();
    if (!normalizedShiftId) return null;
    const markers = getPersistedBiddingUnassignedShiftMarkers();
    return markers[normalizedShiftId] ?? null;
};

const DEFAULT_BIDDING_INVITATION_TIMEOUT_MINUTES = 180;

const getBiddingTimeoutMinutesFromConfig = (getEnvironmentInfo?: () => any) => {
    if (typeof window === 'undefined') return null;
    try {
        if (!getEnvironmentInfo) return null;
        const envInfo = getEnvironmentInfo();
        const raw = envInfo?.configuration?.jobBiddingModule?.biddingInvitationsTimeoutInMinutes;
        if (raw == null || String(raw).trim() === '') return DEFAULT_BIDDING_INVITATION_TIMEOUT_MINUTES;
        const timeoutMinutes = Number(raw);
        if (!Number.isFinite(timeoutMinutes) || timeoutMinutes <= 0) return DEFAULT_BIDDING_INVITATION_TIMEOUT_MINUTES;
        return Math.max(1, Math.floor(timeoutMinutes));
    } catch {
        return DEFAULT_BIDDING_INVITATION_TIMEOUT_MINUTES;
    }
};

const compactToolbarButtonStyles: Partial<IButtonStyles> = {
    root: {
        minHeight: 32,
        height: 32,
        minWidth: 0,
        paddingLeft: 6,
        paddingRight: 6,
        borderRadius: 6
    },
    rootHovered: {
        borderRadius: 6
    },
    rootPressed: {
        borderRadius: 6
    },
    rootDisabled: {
        borderRadius: 6
    },
    flexContainer: {
        justifyContent: 'center',
        columnGap: '3px'
    },
    textContainer: {
        padding: 0
    },
    label: {
        fontSize: 12,
        lineHeight: 1.05,
        margin: 0,
        whiteSpace: 'nowrap'
    },
    menuIcon: {
        marginLeft: 2,
        marginRight: 0,
        fontSize: 10
    }
};

const compactToolbarIconButtonStyles: Partial<IButtonStyles> = {
    root: {
        width: 34,
        minWidth: 34,
        minHeight: 32,
        height: 32,
        padding: 0,
        borderRadius: 6
    },
    rootHovered: {
        borderRadius: 6
    },
    rootPressed: {
        borderRadius: 6
    },
    rootDisabled: {
        borderRadius: 6
    },
    flexContainer: {
        justifyContent: 'center'
    },
    icon: {
        margin: 0,
        fontSize: 14
    }
};
const roundedActionButtonStyles: Partial<IButtonStyles> = {
    root: {
        borderRadius: 999,
        minHeight: 32,
        paddingLeft: 14,
        paddingRight: 14
    },
    rootHovered: { borderRadius: 999 },
    rootPressed: { borderRadius: 999 },
    rootDisabled: { borderRadius: 999 }
};
const openInvitationsCrmButtonStyles: Partial<IButtonStyles> = {
    root: {
        borderRadius: 999,
        minHeight: 26,
        height: 26,
        paddingLeft: 12,
        paddingRight: 12,
        backgroundColor: '#eff6ff',
        borderColor: '#bfdbfe',
        color: '#1d4ed8'
    },
    rootHovered: {
        borderRadius: 999,
        backgroundColor: '#dbeafe',
        borderColor: '#93c5fd',
        color: '#1d4ed8'
    },
    rootPressed: {
        borderRadius: 999,
        backgroundColor: '#bfdbfe',
        borderColor: '#60a5fa',
        color: '#1e3a8a'
    },
    rootDisabled: { borderRadius: 999 },
    label: {
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap'
    },
    flexContainer: {
        gap: 4
    }
};
const assignedButtonStyles: Partial<IButtonStyles> = {
    root: {
        borderRadius: 999,
        minHeight: 32,
        paddingLeft: 14,
        paddingRight: 14,
        backgroundColor: '#16a34a',
        borderColor: '#16a34a',
        color: '#ffffff'
    },
    rootHovered: {
        backgroundColor: '#15803d',
        borderColor: '#15803d',
        color: '#ffffff'
    },
    rootPressed: {
        backgroundColor: '#166534',
        borderColor: '#166534',
        color: '#ffffff'
    },
    rootDisabled: {
        borderRadius: 999,
        minHeight: 32,
        paddingLeft: 14,
        paddingRight: 14,
        backgroundColor: '#16a34a',
        borderColor: '#16a34a',
        color: '#ffffff',
        opacity: 1
    }
};

const parseRange = (start: unknown, end: unknown): ITimeRange | null => {
    const startMs = new Date(String(start ?? '')).getTime();
    const endMsRaw = new Date(String(end ?? start ?? '')).getTime();
    if (Number.isNaN(startMs)) return null;
    const endMs = Number.isNaN(endMsRaw) ? startMs : endMsRaw;
    if (endMs <= startMs) return null;
    return { startMs, endMs };
}

const hasOverlap = (a: ITimeRange, b: ITimeRange): boolean => a.startMs < b.endMs && a.endMs > b.startMs;

const clampMatchPercent = (value: unknown): number => {
    const asNumber = Number(value);
    if (!Number.isFinite(asNumber)) return 0;
    return Math.max(0, Math.min(100, Math.round(asNumber)));
}

const asDateTimeLabel = (value: string | null | undefined): string => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return format(parsed, 'dd MMM yyyy, HH:mm');
}

const asDateLabel = (value: Date | string | null | undefined): string | undefined => {
    if (!value) return undefined;
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return undefined;
    return format(parsed, 'dd MMM yyyy');
}

const buildDurationLabelFromRange = (start: string | null, end: string | null): string => {
    if (!start || !end) return '-';
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (Number.isNaN(s) || Number.isNaN(e) || e <= s) return '-';
    const totalMinutes = Math.round((e - s) / 60000);
    if (totalMinutes < 60) return `${totalMinutes}m`;
    const hours = totalMinutes / 60;
    return `${hours % 1 === 0 ? hours.toFixed(0) : hours.toFixed(1)}h`;
}

const padTimerSegment = (value: number): string => `${Math.max(0, Math.floor(value))}`.padStart(2, '0');

const formatAutoAssignCountdown = (targetIso: string | null | undefined, nowMs: number): string => {
    if (!targetIso) return '-';
    const targetMs = new Date(targetIso).getTime();
    if (Number.isNaN(targetMs)) return '-';
    const remainingMs = targetMs - nowMs;
    if (remainingMs <= 0) return 'Due now';
    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (days > 0) {
        return `${days}d ${padTimerSegment(hours)}:${padTimerSegment(minutes)}:${padTimerSegment(seconds)}`;
    }
    return `${padTimerSegment(hours)}:${padTimerSegment(minutes)}:${padTimerSegment(seconds)}`;
}

const formatShiftStartCountdown = (targetIso: string | null | undefined, nowMs: number): string => {
    if (!targetIso) return '-';
    const targetMs = new Date(targetIso).getTime();
    if (Number.isNaN(targetMs)) return '-';
    const remainingMs = targetMs - nowMs;
    if (remainingMs <= 0) return 'Started';
    return formatAutoAssignCountdown(targetIso, nowMs);
}

const buildSmartResourceDetails = (resource?: ISmartResourceLike): ISmartResourceDetails | undefined => {
    if (!resource) return undefined;

    const skills: ISmartResourceSkill[] = [];
    (resource.characteristics ?? []).forEach((skill) => {
        const name = String(skill?.name ?? '').trim();
        if (!name) return;
        const rating = String(skill?.ratingName ?? '').trim();
        skills.push({
            label: rating ? `${name} (${rating})` : name,
            expiryDate: asDateLabel(skill?.expiryDate)
        });
    });

    const categories = (resource.categories ?? [])
        .map((category) => String(category?.name ?? '').trim())
        .filter((value) => value.length > 0);

    const siteLocations = (resource.siteLocation ?? [])
        .map((site) => String(site?.illumina_name ?? '').trim())
        .filter((value) => value.length > 0);

    return {
        skills,
        categories,
        siteLocations,
        minimumWorkHoursPerPayroll: resource.minimumWorkHoursPerPayroll ?? null,
        maximumWorkHourPerPayroll: resource.maximumWorkHourPerPayroll ?? null,
        maxHoursPerShift: resource.maxHoursPerShift ?? null,
        personalNote: resource.personalNote ?? null
    };
}

const looksLikeGuid = (value: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const normalizeSkillSet = (skills: unknown): Set<string> => {
    const normalized = new Set<string>();
    if (!Array.isArray(skills)) return normalized;

    skills.forEach((skill: any) => {
        const raw = typeof skill === 'string'
            ? skill
            : String(skill?.name ?? '');
        const clean = raw.trim().toLowerCase();
        if (!clean || looksLikeGuid(clean)) return;
        normalized.add(clean);
    });

    return normalized;
}

const isHumanAssignableResource = (resource: any): boolean => {
    const name = String(resource?.name ?? resource?.title ?? '').trim().toLowerCase();
    if (!name) return false;
    if (name.includes('vehicle')) return false;
    if (name.includes('room')) return false;
    if (name.includes('respite')) return false;
    return true;
}

const buildSmartSuggestions = (payload: AIEmpowerPayload, allocations: IAiAllocation[]): ISmartSuggestion => {
    const jobRanges = new Map<string, ITimeRange>();
    const jobById = new Map<string, any>();
    (payload.unassignedJobs ?? []).forEach((job: any) => {
        const jobId = String(job?.jobId ?? '');
        if (!jobId) return;
        const range = parseRange(job?.start, job?.end);
        if (!range) return;
        jobRanges.set(jobId, range);
        jobById.set(jobId, job);
    });

    const busyRangesByResource = new Map<string, ITimeRange[]>();
    const addBusyRange = (resourceId: string, range: ITimeRange) => {
        const existing = busyRangesByResource.get(resourceId) ?? [];
        existing.push(range);
        busyRangesByResource.set(resourceId, existing);
    };

    (payload.bookings ?? []).forEach((booking: any) => {
        const resourceId = String(booking?.resourceId ?? '');
        if (!resourceId) return;
        const range = parseRange(booking?.start, booking?.end ?? booking?.start);
        if (!range) return;
        addBusyRange(resourceId, range);
    });

    (payload.resources ?? []).forEach((resource: any) => {
        const resourceId = String(resource?.id ?? '');
        if (!resourceId) return;
        (resource?.approvedLeaves ?? []).forEach((leave: any) => {
            const range = parseRange(leave?.start, leave?.end);
            if (!range) return;
            addBusyRange(resourceId, range);
        });
    });

    const candidateMapByJob = new Map<string, IAiAllocation[]>();
    allocations.forEach((allocation) => {
        const jobId = String(allocation?.jobId ?? '');
        const resourceId = String(allocation?.resourceId ?? '');
        if (!jobId || !resourceId) return;
        const existing = candidateMapByJob.get(jobId) ?? [];
        const duplicateIndex = existing.findIndex((candidate) => candidate.resourceId === resourceId);
        if (duplicateIndex > -1) {
            if (existing[duplicateIndex].matchPercent >= allocation.matchPercent) return;
            existing[duplicateIndex] = allocation;
            candidateMapByJob.set(jobId, existing);
            return;
        }
        existing.push(allocation);
        candidateMapByJob.set(jobId, existing);
    });

    // Add deterministic availability-based fallback candidates so one run can still
    // propose matches when AI does not return enough unique options for every job.
    const fallbackLimitPerJob = 8;
    Array.from(jobRanges.entries()).forEach(([jobId, jobRange]) => {
        const job = jobById.get(jobId);
        const existingCandidates = candidateMapByJob.get(jobId) ?? [];
        const existingIds = new Set(existingCandidates.map((candidate) => String(candidate.resourceId ?? '')));
        const requiredSkills = normalizeSkillSet(job?.requiredSkills);

        const fallbackCandidates: IAiAllocation[] = [];
        (payload.resources ?? []).forEach((resource: any) => {
            const resourceId = String(resource?.id ?? '');
            if (!resourceId || existingIds.has(resourceId)) return;
            if (!isHumanAssignableResource(resource)) return;

            const busyRanges = busyRangesByResource.get(resourceId) ?? [];
            const isBusy = busyRanges.some((busyRange) => hasOverlap(busyRange, jobRange));
            if (isBusy) return;

            const resourceSkills = normalizeSkillSet(resource?.skills ?? resource?.characteristics ?? []);
            let matchedSkills = 0;
            if (requiredSkills.size > 0) {
                requiredSkills.forEach((skill) => {
                    if (resourceSkills.has(skill)) matchedSkills += 1;
                });
            }

            const skillRatio = requiredSkills.size > 0 ? matchedSkills / requiredSkills.size : 0;
            const fallbackScore = requiredSkills.size > 0
                ? clampMatchPercent(45 + Math.round(skillRatio * 25))
                : 60;
            const fallbackReason = requiredSkills.size === 0
                ? 'Fallback suggestion: available in timeframe with no overlapping bookings or leave.'
                : matchedSkills > 0
                    ? `Fallback suggestion: available in timeframe with ${matchedSkills}/${requiredSkills.size} required skills matched.`
                    : 'Fallback suggestion: available in timeframe; required skills could not be confirmed from payload.';

            fallbackCandidates.push({
                jobId,
                resourceId,
                start: new Date(jobRange.startMs).toISOString(),
                end: new Date(jobRange.endMs).toISOString(),
                reason: fallbackReason,
                matchPercent: fallbackScore
            });
        });

        fallbackCandidates.sort((a, b) => clampMatchPercent(b.matchPercent) - clampMatchPercent(a.matchPercent));
        if (fallbackCandidates.length > 0) {
            candidateMapByJob.set(jobId, [...existingCandidates, ...fallbackCandidates.slice(0, fallbackLimitPerJob)]);
        }
    });

    const allJobIds = Array.from(jobRanges.keys());
    allJobIds.sort((a, b) => {
        const aCandidates = candidateMapByJob.get(a)?.length ?? 0;
        const bCandidates = candidateMapByJob.get(b)?.length ?? 0;
        if (aCandidates !== bCandidates) return aCandidates - bCandidates;
        const aStart = jobRanges.get(a)?.startMs ?? Number.MAX_SAFE_INTEGER;
        const bStart = jobRanges.get(b)?.startMs ?? Number.MAX_SAFE_INTEGER;
        return aStart - bStart;
    });

    const assignments: IAiAllocation[] = [];
    const unassigned: string[] = [];

    allJobIds.forEach((jobId) => {
        const baseRange = jobRanges.get(jobId);
        if (!baseRange) {
            unassigned.push(jobId);
            return;
        }

        const orderedCandidates = (candidateMapByJob.get(jobId) ?? [])
            .slice()
            .sort((a, b) => clampMatchPercent(b.matchPercent) - clampMatchPercent(a.matchPercent));

        let selected: IAiAllocation | null = null;
        let selectedRange: ITimeRange | null = null;

        for (const candidate of orderedCandidates) {
            const resourceId = String(candidate?.resourceId ?? '');
            if (!resourceId) continue;
            const candidateRange = parseRange(candidate?.start, candidate?.end) ?? baseRange;
            if (!candidateRange) continue;

            const busyRanges = busyRangesByResource.get(resourceId) ?? [];
            const isBusy = busyRanges.some((busyRange) => hasOverlap(busyRange, candidateRange));
            if (isBusy) continue;

            selected = {
                jobId,
                resourceId,
                start: new Date(candidateRange.startMs).toISOString(),
                end: new Date(candidateRange.endMs).toISOString(),
                reason: String(candidate?.reason ?? '').trim() || 'Suggested based on the best available fit.',
                matchPercent: clampMatchPercent(candidate?.matchPercent)
            };
            selectedRange = candidateRange;
            break;
        }

        if (!selected || !selectedRange) {
            unassigned.push(jobId);
            return;
        }

        assignments.push(selected);
        addBusyRange(selected.resourceId, selectedRange);
    });

    return { assignments, unassigned };
}

const DraftControl = () => {
    const {
        draftStatus,
        deleteDraft,
        saveDraft,
        loadDraft,
        undoShiftMove,
        validateAndPublishShifts,
        reloadShifts,
        refreshBiddingSnapshotById,
        validateAllShifts,
        getShiftsForCalendarView,
        startDate,
        endDate,
        moveShift,
        updateShiftInfo,
        getShift,
        getEnvironmentInfo,
        siteLocationCoordinates
    } = useContext(shiftsContext)
    const { getAllResources } = useContext(resourcesContext)
    const { openRsoConfiguration, activePresetLabel, clearManualOverrideLevels, activeLevels } = useRsoConfiguration();

    const [smartFindLoading, setSmartFindLoading] = useState(false)
    const [smartFindProgress, setSmartFindProgress] = useState('Finding smart matches...')
    const [decisionLoadingByJob, setDecisionLoadingByJob] = useState<Record<string, boolean>>({})
    const [assignAllLoading, setAssignAllLoading] = useState(false)
    const [isMatchModalOpen, setIsMatchModalOpen] = useState(false)
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false)
    const [currentBatch, setCurrentBatch] = useState<ISmartMatchBatch | null>(null)
    const [smartMatchHistory, setSmartMatchHistory] = useState<ISmartMatchHistoryEntry[]>([])
    const [expandedResourceByJob, setExpandedResourceByJob] = useState<Record<string, boolean>>({})
    const [smartFindRunRanges, setSmartFindRunRanges] = useState<Record<string, true>>({})
    const [biddingAssignmentsView, setBiddingAssignmentsView] = useState<TBiddingAssignmentsView | null>(null)
    const [invitationClockMs, setInvitationClockMs] = useState(() => Date.now())
    const lastAiStatusToastRef = useRef('')
    const lastBiddingSummaryRef = useRef('')
    const openInvitationRefreshInFlightRef = useRef<Set<string>>(new Set())
    const currentSmartRangeKey = useMemo(() => `${startDate.getTime()}_${endDate.getTime()}`, [startDate, endDate]);
    const hasSmartFindRun = !!smartFindRunRanges[currentSmartRangeKey];
    const isBiddingDebugEnabled = () => {
        if (typeof window === 'undefined') return false;
        try {
            const envToggle = (window as any).__RB_BIDDING_DEBUG;
            if (envToggle === true || envToggle === 1 || envToggle === '1') return true;
            return ['1', 'true'].includes(window.localStorage?.getItem('__RB_BIDDING_DEBUG') ?? window.localStorage?.getItem('rb_bidding_debug') ?? '');
        } catch {
            return false;
        }
    };
    const logBiddingDebug = (message: string, data?: unknown) => {
        if (!isBiddingDebugEnabled()) return;
        if (typeof data === 'undefined') {
            console.log(`[ShiftBiddingDebug] ${message}`);
            return;
        }
        console.log(`[ShiftBiddingDebug] ${message}`, data);
    };
    const isLocalBiddingHost = () => {
        if (typeof window === 'undefined') return false;
        const host = String(window.location?.hostname ?? '').trim().toLowerCase();
        return host === 'localhost' || host === '127.0.0.1';
    };
    const isBiddingPollingEnabled = () => {
        if (!isLocalBiddingHost()) return true;
        try {
            const envToggle = (window as any).__RB_ENABLE_BIDDING_POLLING;
            if (envToggle === true || envToggle === 1 || envToggle === '1') return true;
            return ['1', 'true'].includes(window.localStorage?.getItem('__RB_ENABLE_BIDDING_POLLING') ?? window.localStorage?.getItem('rb_enable_bidding_polling') ?? '');
        } catch {
            return false;
        }
    };
    const biddingAssignments = useMemo(() => {
        const nowMs = Date.now();
        const boardRangeStartMs = new Date(startDate as any).getTime();
        const boardRangeEndMs = new Date(endDate as any).getTime();
        const hasValidBoardRange = Number.isFinite(boardRangeStartMs) && Number.isFinite(boardRangeEndMs);
        const activeInvitationStatuses = new Set(['expressed interest', 'invited']);
        const invitationLifecycleStatuses = new Set([
            ...Array.from(activeInvitationStatuses),
            'accepted',
            'declined',
            'expired',
            'bid withdrawn',
            'bid failed'
        ]);
        const resourceMap = new Map<string, string>();
        (getAllResources() ?? []).forEach((resource: any) => {
            const resourceId = String(resource?.id ?? '').trim();
            if (!resourceId) return;
            const label = String(resource?.title ?? '').trim();
            if (!label) return;
            resourceMap.set(resourceId, label);
        });
        const normalizeInvitationStatus = (value: unknown) => String(value ?? '').trim().toLocaleLowerCase();
        const invitationStatusLabel = (status: string) => {
            switch (status) {
                case 'opened':
                    return 'Opened';
                case 'viewed':
                    return 'Viewed';
                case 'expressed interest':
                    return 'Expressed Interest';
                case 'invited':
                    return 'Invited';
                case 'accepted':
                    return 'Accepted';
                case 'declined':
                case 'bid withdrawn':
                    return 'Declined';
                case 'expired':
                case 'bid failed':
                    return 'Expired';
                default:
                    return 'Unknown';
            }
        };
        const invitationStatusPriority = (status: string) => {
            switch (status) {
                case 'expressed interest':
                case 'opened':
                case 'viewed':
                case 'invited':
                    return 0;
                case 'accepted':
                    return 1;
                case 'declined':
                case 'bid withdrawn':
                    return 2;
                case 'expired':
                case 'bid failed':
                    return 3;
                default:
                    return 4;
            }
        };

        return (getShiftsForCalendarView() ?? [])
            .filter((shift) => {
                const bidderList = Array.isArray(shift.bidders) ? shift.bidders : [];
                const hasInvitationHistory = bidderList.some((bidder: any) =>
                    invitationLifecycleStatuses.has(normalizeInvitationStatus((bidder as any)?.bookingStatus))
                );
                if (shift.display === 'unavailable' || shift.display === 'contractedHours' || (!shift.isJobBidding && !hasInvitationHistory)) {
                    return false;
                }
                if (!hasValidBoardRange) {
                    return true;
                }

                const shiftStartMs = new Date(shift.start as any).getTime();
                const shiftEndRawMs = new Date((shift.end ?? shift.start) as any).getTime();
                const shiftEndMs = Number.isNaN(shiftEndRawMs) ? shiftStartMs : shiftEndRawMs;
                if (Number.isNaN(shiftStartMs)) {
                    return false;
                }

                return shiftStartMs <= boardRangeEndMs && shiftEndMs >= boardRangeStartMs;
            })
            .map((shift): IBiddingAssignmentItem | null => {
                const startMs = new Date(shift.start as any).getTime();
                const endMsRaw = new Date((shift.end ?? shift.start) as any).getTime();
                const endMs = Number.isNaN(endMsRaw) ? startMs : endMsRaw;
                const isPast = Number.isFinite(endMs) && endMs < nowMs;
                const startIso = Number.isNaN(startMs) ? null : new Date(startMs).toISOString();
                const biddingTimeoutMinutes = getBiddingTimeoutMinutesFromConfig(getEnvironmentInfo);
                const marker = getPersistedBiddingUnassignedShiftMarker(String(shift.id ?? '').trim());
                const fallbackStartedAt = typeof shift.start === 'string'
                    ? shift.start
                    : (startIso ?? new Date().toISOString());
                const markerStartedAt = marker?.startedAt ?? '';
                const resolvedStartedAt = Number.isNaN(Date.parse(markerStartedAt)) ? fallbackStartedAt : markerStartedAt;
                const markerStartedAtMs = Number.isNaN(Date.parse(resolvedStartedAt ?? ''))
                    ? null
                    : new Date(resolvedStartedAt).getTime();
                const autoAssignFromConfig = biddingTimeoutMinutes && markerStartedAtMs !== null
                    ? new Date(markerStartedAtMs + (biddingTimeoutMinutes * 60 * 1000)).toISOString()
                    : null;

                const endIso = Number.isNaN(endMs) ? startIso : new Date(endMs).toISOString();
                if (!startIso) return null;

                const selectedResourceId = String(shift.resourceId ?? '').trim();
                const hasSelectedResource = selectedResourceId.length > 0 && selectedResourceId !== '0';
                const inviteeByResource = new Map<string, IBiddingInviteeItem & { priority: number }>();
                (Array.isArray(shift.bidders) ? shift.bidders : []).forEach((bidder: any) => {
                    const resourceId = String(bidder?.bookableResourceId ?? '').trim();
                    if (!resourceId) return;
                    const bookingStatus = normalizeInvitationStatus(bidder?.bookingStatus);
                    if (!invitationLifecycleStatuses.has(bookingStatus)) return;
                    const invitationSeenAtRaw = String((bidder as any)?.invitationSeenAt ?? '').trim();
                    const invitationOpenedAtRaw = String((bidder as any)?.invitationOpenedAt ?? '').trim();
                    const invitationSeenAt = invitationSeenAtRaw && !Number.isNaN(Date.parse(invitationSeenAtRaw))
                        ? invitationSeenAtRaw
                        : null;
                    const invitationOpenedAt = invitationOpenedAtRaw && !Number.isNaN(Date.parse(invitationOpenedAtRaw))
                        ? invitationOpenedAtRaw
                        : null;
                    const displayStatus = bookingStatus === 'invited'
                        ? (invitationOpenedAt
                            ? 'opened'
                            : invitationSeenAt
                                ? 'viewed'
                                : 'invited')
                        : bookingStatus;
                    const candidatePriority = invitationStatusPriority(bookingStatus);
                    const candidateInvitee = {
                        resourceId,
                        resourceName: resourceMap.get(resourceId) || 'Unknown resource',
                        status: displayStatus,
                        statusLabel: invitationStatusLabel(displayStatus),
                        isActive: activeInvitationStatuses.has(bookingStatus),
                        invitationSeenAt,
                        invitationOpenedAt,
                        priority: candidatePriority
                    };
                    const existing = inviteeByResource.get(resourceId);
                    if (!existing || candidatePriority < existing.priority) {
                        inviteeByResource.set(resourceId, candidateInvitee);
                    }
                });
                const selectedResourceName = hasSelectedResource
                    ? (
                        resourceMap.get(selectedResourceId)
                        || String(shift.resourcePublishedAs ?? '').trim()
                        || 'Selected staff'
                    )
                    : undefined;
                const invitees = Array.from(inviteeByResource.values())
                    .sort((a, b) => {
                        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
                        if (a.priority !== b.priority) return a.priority - b.priority;
                        return a.resourceName.localeCompare(b.resourceName);
                    })
                    .map(({ priority, ...invitee }) => {
                        let outcomeLabel: string | null = null;
                        if (hasSelectedResource) {
                            const isSelectedInvitee = invitee.resourceId === selectedResourceId;
                            const shouldShowOutcomeLabel = ['invited', 'opened', 'viewed', 'expressed interest'].includes(invitee.status);
                            if (shouldShowOutcomeLabel) {
                                if (isSelectedInvitee) {
                                    outcomeLabel = invitee.status === 'expressed interest' ? 'Chosen' : 'Accepted';
                                } else {
                                    outcomeLabel = invitee.status === 'expressed interest' ? 'Not chosen' : 'Not accepted';
                                }
                            }
                        }

                        return {
                            ...invitee,
                            outcomeLabel
                        };
                    });

                const invitationResourceCount = invitees.length;
                const activeInvitationCount = invitees.filter((invitee) => invitee.isActive).length;
                const selectedFromInvitedPool = hasSelectedResource && invitationResourceCount > 0;

                return {
                    jobId: shift.id,
                    title: String(shift.title ?? '').trim() || 'Untitled shift',
                    start: startIso,
                    end: endIso,
                    autoAssignAt: autoAssignFromConfig ?? startIso,
                    selectedResourceId,
                    selectedResourceName,
                    invitationResourceCount,
                    activeInvitationCount,
                    hasSelectedResource,
                    selectedFromInvitedPool,
                    isPast,
                    invitees
                };
            })
            .filter((item): item is IBiddingAssignmentItem => !!item)
            .sort((a, b) => {
                const aMs = new Date(a.start ?? '').getTime();
                const bMs = new Date(b.start ?? '').getTime();
                if (Number.isNaN(aMs) && Number.isNaN(bMs)) return 0;
                if (Number.isNaN(aMs)) return 1;
                if (Number.isNaN(bMs)) return -1;
                return aMs - bMs;
            });
    }, [endDate, getAllResources, getShiftsForCalendarView, startDate]);
    const biddingInvitations = useMemo(
        () => biddingAssignments.filter((item) => item.invitationResourceCount > 0),
        [biddingAssignments]
    );
    const openBiddingAssignments = useMemo(
        () => biddingInvitations.filter((item) => item.activeInvitationCount > 0 && !item.hasSelectedResource),
        [biddingInvitations]
    );
    const openBiddingJobIds = useMemo(
        () => Array.from(
            new Set(
                openBiddingAssignments
                    .map((item) => String(item.jobId ?? '').trim())
                    .filter((jobId) => jobId.length > 0)
            )
        ).sort(),
        [openBiddingAssignments]
    );
    const openBiddingJobIdsKey = useMemo(
        () => openBiddingJobIds.join('|'),
        [openBiddingJobIds]
    );
    const openBiddingCountdownLabel = useMemo(() => {
        if (openBiddingAssignments.length < 1) return '';
        return formatAutoAssignCountdown(openBiddingAssignments[0]?.autoAssignAt, invitationClockMs);
    }, [openBiddingAssignments, invitationClockMs]);
    const openInvitationsButtonLabel = useMemo(() => {
        if (openBiddingAssignments.length < 1) return 'Open Invitations';
        const suffix = openBiddingCountdownLabel && openBiddingCountdownLabel !== '-'
            ? ` (${openBiddingAssignments.length} • ${openBiddingCountdownLabel})`
            : ` (${openBiddingAssignments.length})`;
        return `Open Invitations${suffix}`;
    }, [openBiddingAssignments.length, openBiddingCountdownLabel]);
    const pastBiddingAssignments = useMemo(
        () => biddingInvitations.filter((item) => item.hasSelectedResource || item.activeInvitationCount === 0),
        [biddingInvitations]
    );
    useEffect(() => {
        const summary = {
            total: biddingAssignments.length,
            invitations: biddingInvitations.length,
            open: openBiddingAssignments.length,
            past: pastBiddingAssignments.length
        };
        const summaryKey = JSON.stringify(summary);
        if (lastBiddingSummaryRef.current === summaryKey) return;
        lastBiddingSummaryRef.current = summaryKey;
        logBiddingDebug('biddingAssignments:summary', summary);
    }, [biddingAssignments, biddingInvitations, openBiddingAssignments, pastBiddingAssignments]);

    useEffect(() => {
        if (!biddingAssignmentsView) return;
        const openCount = openBiddingAssignments.length;
        const pastCount = pastBiddingAssignments.length;
        const currentItems = biddingAssignmentsView === 'open' ? openBiddingAssignments : pastBiddingAssignments;
        logBiddingDebug('openInvitations:modal-opened', {
            view: biddingAssignmentsView,
            total: biddingAssignmentsView === 'open' ? openCount : pastCount,
            openCount,
            pastCount,
            items: currentItems.map((item) => ({
                jobId: item.jobId,
                autoAssignAt: item.autoAssignAt,
                shiftStart: item.start
            }))
        });
        return () => {
            logBiddingDebug('openInvitations:modal-closed', {
                view: biddingAssignmentsView,
                total: biddingAssignmentsView === 'open' ? openCount : pastCount
            });
        };
    }, [biddingAssignmentsView, openBiddingAssignments, pastBiddingAssignments]);

    useEffect(() => {
        if (!openBiddingAssignments.length) return;
        setInvitationClockMs(Date.now());
        const intervalId = window.setInterval(() => {
            setInvitationClockMs(Date.now());
        }, 1000);
        return () => {
            window.clearInterval(intervalId);
        };
    }, [openBiddingAssignments.length]);

    useEffect(() => {
        if (openBiddingJobIds.length < 1) {
            logBiddingDebug('openInvites:poll-skipped', {
                reason: 'no-open-invitations',
                jobIds: []
            });
            return;
        }

        let cancelled = false;
        const activeJobIds = [...openBiddingJobIds];
        const pollingEnabled = isBiddingPollingEnabled();
        logBiddingDebug('openInvites:poll-start', {
            jobIds: activeJobIds,
            intervalMs: pollingEnabled ? 15000 : 0,
            pollingEnabled
        });

        const refreshOpenInvitationShifts = async () => {
            if (cancelled) return;
            if (typeof document !== 'undefined' && document.hidden) {
                return;
            }

            await Promise.all(activeJobIds.map(async (jobId) => {
                const inFlight = openInvitationRefreshInFlightRef.current;
                if (inFlight.has(jobId)) return;
                inFlight.add(jobId);
                try {
                    await refreshBiddingSnapshotById(jobId);
                } catch (error) {
                    logBiddingDebug('openInvites:refresh-failed', {
                        jobId,
                        error: (error as any)?.message ?? String(error)
                    });
                } finally {
                    inFlight.delete(jobId);
                }
            }));
        };

        void refreshOpenInvitationShifts();
        if (!pollingEnabled) {
            return () => {
                cancelled = true;
            };
        }
        const intervalId = window.setInterval(() => {
            void refreshOpenInvitationShifts();
        }, 15000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [openBiddingJobIdsKey, refreshBiddingSnapshotById]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(SMART_MATCH_HISTORY_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                setSmartMatchHistory(parsed as ISmartMatchHistoryEntry[]);
            }
        } catch {
            setSmartMatchHistory([]);
        }
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(SMART_MATCH_HISTORY_KEY, JSON.stringify(smartMatchHistory.slice(0, SMART_MATCH_HISTORY_MAX)));
        } catch {
            // Ignore storage errors.
        }
    }, [smartMatchHistory]);

    const clearSmartHighlightForJobs = (jobIds: string[]) => {
        jobIds.forEach((jobId) => {
            if (!jobId) return;
            updateShiftInfo(jobId, { smartMatched: false });
        });
    }

    const pushHistoryEntries = (entries: ISmartMatchHistoryEntry[]) => {
        if (!entries.length) return;
        setSmartMatchHistory((prev) => [...entries, ...prev].slice(0, SMART_MATCH_HISTORY_MAX));
    }

    const setJobDecisionState = (jobId: string, status: TSmartDecision, updatedAt: string) => {
        setCurrentBatch((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                items: prev.items.map((item) => item.jobId === jobId ? { ...item, status, updatedAt } : item)
            };
        });
    }

    const appendDecisionHistory = (item: ISmartMatchItem, action: 'accepted' | 'rejected') => {
        const timestamp = new Date().toISOString();
        const batchId = currentBatch?.id ?? 'unknown';
        const historyEntry: ISmartMatchHistoryEntry = {
            id: `${batchId}_${item.jobId}_${action}_${timestamp}`,
            batchId,
            timestamp,
            action,
            jobId: item.jobId,
            jobTitle: item.title,
            shiftStart: item.start,
            shiftEnd: item.end,
            resourceId: item.suggestedResourceId,
            resourceName: item.suggestedResourceName,
            matchPercent: item.matchPercent,
            reason: item.reason
        };
        pushHistoryEntries([historyEntry]);
    }

    const toggleResourceDetails = (jobId: string) => {
        setExpandedResourceByJob((prev) => ({
            ...prev,
            [jobId]: !prev[jobId]
        }));
    }

    const handleAssignOne = async (item: ISmartMatchItem) => {
        if (!item.suggestedResourceId) {
            toast('warning', 'No suggested resource to assign for this shift.');
            return;
        }

        setDecisionLoadingByJob((prev) => ({ ...prev, [item.jobId]: true }));
        try {
            const moved = await moveShift(item.jobId, item.suggestedResourceId, undefined, true);
            if (!moved) {
                toast('error', `Unable to assign "${item.title}".`);
                return;
            }

            const updatedAt = new Date().toISOString();
            setJobDecisionState(item.jobId, 'accepted', updatedAt);
            appendDecisionHistory(item, 'accepted');
            toast('success', `Assigned "${item.title}" to ${item.suggestedResourceName ?? 'selected resource'}.`);
        } finally {
            setDecisionLoadingByJob((prev) => {
                const next = { ...prev };
                delete next[item.jobId];
                return next;
            });
        }
    }

    const handleRejectOne = async (item: ISmartMatchItem) => {
        setDecisionLoadingByJob((prev) => ({ ...prev, [item.jobId]: true }));
        try {
            const moved = await moveShift(item.jobId, '0', undefined, false);
            if (!moved) {
                toast('error', `Unable to reject "${item.title}".`);
                return;
            }

            const updatedAt = new Date().toISOString();
            setJobDecisionState(item.jobId, 'rejected', updatedAt);
            appendDecisionHistory(item, 'rejected');
            toast('success', `"${item.title}" moved back to Unassigned.`);
        } finally {
            setDecisionLoadingByJob((prev) => {
                const next = { ...prev };
                delete next[item.jobId];
                return next;
            });
        }
    }

    const handleChooseInvitation = async (item: IBiddingAssignmentItem, invitee: IBiddingInviteeItem) => {
        const resourceId = String(invitee.resourceId ?? '').trim();
        if (!resourceId) {
            toast('warning', 'No resource is available to choose for this invitation.');
            return;
        }

        const shift = getShift(item.jobId);
        if (!shift) {
            toast('error', 'Unable to find this shift on the current board.');
            return;
        }

        if (!shift.isJobBidding) {
            toast('info', 'This shift is no longer open for bidding.');
            return;
        }

        setDecisionLoadingByJob((prev) => ({ ...prev, [item.jobId]: true }));
        try {
            const moved = await moveShift(item.jobId, resourceId);
            if (!moved) {
                toast('error', `Unable to choose ${invitee.resourceName} for "${item.title}".`);
                return;
            }

            toast('success', `Selected ${invitee.resourceName} for "${item.title}". Publish to save the assignment.`);
        } finally {
            setDecisionLoadingByJob((prev) => {
                const next = { ...prev };
                delete next[item.jobId];
                return next;
            });
        }
    }

    const handleAssignAll = async () => {
        if (!currentBatch) return;
        const pending = currentBatch.items.filter((item) => item.status === 'pending' && !!item.suggestedResourceId);
        if (!pending.length) {
            toast('info', 'No pending suggested shifts to assign.');
            return;
        }

        setAssignAllLoading(true);
        try {
            let assigned = 0;
            let failed = 0;
            const successfulIds = new Set<string>();
            const historyEntries: ISmartMatchHistoryEntry[] = [];
            const timestamp = new Date().toISOString();

            for (const item of pending) {
                const moved = await moveShift(item.jobId, item.suggestedResourceId, undefined, true);
                if (!moved) {
                    failed += 1;
                    continue;
                }

                assigned += 1;
                successfulIds.add(item.jobId);
                historyEntries.push({
                    id: `${currentBatch.id}_${item.jobId}_accepted_${timestamp}`,
                    batchId: currentBatch.id,
                    timestamp,
                    action: 'accepted',
                    jobId: item.jobId,
                    jobTitle: item.title,
                    shiftStart: item.start,
                    shiftEnd: item.end,
                    resourceId: item.suggestedResourceId,
                    resourceName: item.suggestedResourceName,
                    matchPercent: item.matchPercent,
                    reason: item.reason
                });
            }

            if (successfulIds.size > 0) {
                setCurrentBatch((prev) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        items: prev.items.map((item) => successfulIds.has(item.jobId)
                            ? { ...item, status: 'accepted', updatedAt: timestamp }
                            : item)
                    };
                });
            }

            pushHistoryEntries(historyEntries);
            toast('success', `Assigned ${assigned}. Failed ${failed}.`);
        } finally {
            setAssignAllLoading(false);
        }
    }

    const buildBatchItems = (payload: AIEmpowerPayload, suggestions: ISmartSuggestion): ISmartMatchItem[] => {
        const resourceMap = new Map<string, string>();
        const resourceById = new Map<string, ISmartResourceLike>();
        (getAllResources() ?? []).forEach((resource) => {
            resourceMap.set(resource.id, resource.title);
            resourceById.set(resource.id, resource as unknown as ISmartResourceLike);
        });

        const assignmentByJob = new Map<string, IAiAllocation>();
        suggestions.assignments.forEach((assignment) => {
            assignmentByJob.set(assignment.jobId, assignment);
        });
        const unmatched = new Set<string>(suggestions.unassigned);

        const items = (payload.unassignedJobs ?? []).map((job: any): ISmartMatchItem => {
            const jobId = String(job?.jobId ?? '');
            const shift = getShift(jobId);
            const assignment = assignmentByJob.get(jobId);
            const suggestedResourceId = assignment?.resourceId;
            const title = String(job?.title ?? shift?.title ?? 'Shift');
            const start = (assignment?.start ?? job?.start ?? (shift?.start ? new Date(shift.start).toISOString() : null)) as string | null;
            const end = (assignment?.end ?? job?.end ?? (shift?.end ? new Date(shift.end).toISOString() : null)) as string | null;
            const durationLabel = buildDurationLabelFromRange(start, end);

            if (!assignment || unmatched.has(jobId)) {
                return {
                    jobId,
                    title,
                    start,
                    end,
                    durationLabel,
                    status: 'unmatched'
                };
            }

            return {
                jobId,
                title,
                start,
                end,
                durationLabel,
                suggestedResourceId,
                suggestedResourceName: resourceMap.get(suggestedResourceId ?? '') ?? 'Suggested resource',
                suggestedResourceDetails: buildSmartResourceDetails(resourceById.get(suggestedResourceId ?? '')),
                reason: assignment.reason,
                matchPercent: assignment.matchPercent,
                status: 'pending'
            };
        });

        return items.sort((a, b) => {
            if (a.status === 'unmatched' && b.status !== 'unmatched') return 1;
            if (a.status !== 'unmatched' && b.status === 'unmatched') return -1;
            const aMs = a.start ? new Date(a.start).getTime() : Number.MAX_SAFE_INTEGER;
            const bMs = b.start ? new Date(b.start).getTime() : Number.MAX_SAFE_INTEGER;
            return aMs - bMs;
        });
    }

    const renderDraftButton = () => {
        const menuProps: IContextualMenuProps = {
            items: [
                {
                    key: 'saveDraft',
                    text: 'Save Draft',
                    onClick: saveDraft,
                    disabled: draftStatus !== DraftStatus.empty && draftStatus !== DraftStatus.edited
                },
                {
                    key: 'loadDraft',
                    text: 'Load Draft',
                    onClick: loadDraft,
                    disabled: draftStatus === DraftStatus.empty
                },
                {
                    key: 'discardDraft',
                    text: 'Discard Draft',
                    onClick: deleteDraft,
                    disabled: draftStatus === DraftStatus.empty
                }
            ]
        }

        return <DefaultButton menuProps={menuProps} disabled={smartFindLoading} styles={compactToolbarButtonStyles}>Drafts</DefaultButton>
    }

    const renderBiddingActionsButton = () => {
        const menuProps: IContextualMenuProps = {
            items: [
                {
                    key: 'open-assignments',
                    text: openInvitationsButtonLabel,
                    onClick: () => {
                        logBiddingDebug('openInvitations:open-requested', {
                            view: 'open',
                            jobCount: openBiddingAssignments.length
                        });
                        setBiddingAssignmentsView('open');
                    }
                },
                {
                    key: 'past-assignments',
                    text: 'Past Invitations',
                    onClick: () => {
                        logBiddingDebug('openInvitations:open-requested', {
                            view: 'past',
                            jobCount: pastBiddingAssignments.length
                        });
                        setBiddingAssignmentsView('past');
                    }
                }
            ]
        };

        return (
            <DefaultButton menuProps={menuProps} disabled={smartFindLoading} styles={compactToolbarButtonStyles}>
                Bidding Actions
            </DefaultButton>
        )
    }

    const renderBiddingAssignmentsModal = () => {
        if (!biddingAssignmentsView) return null;
        const isOpenView = biddingAssignmentsView === 'open';
        const title = isOpenView ? 'Open Invitations' : 'Past Invitations';
        const description = isOpenView
            ? 'Shows shifts with active invitations waiting for acceptance.'
            : 'Shows invitation outcomes, including accepted or closed shifts.';
        const items = isOpenView ? openBiddingAssignments : pastBiddingAssignments;

        return (
            <Modal isOpen={!!biddingAssignmentsView} onDismiss={() => {
                logBiddingDebug('openInvitations:close-requested', {
                    view: biddingAssignmentsView,
                    method: 'onDismiss'
                });
                setBiddingAssignmentsView(null);
            }}>
                <div style={{ width: 'min(980px, 94vw)', maxHeight: '88vh', overflow: 'auto', padding: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <div>
                            <div style={{ fontSize: 22, fontWeight: 700 }}>{title}</div>
                            <div style={{ fontSize: 12, color: '#52606d', marginTop: 2 }}>{description}</div>
                        </div>
                        <DefaultButton onClick={() => {
                            logBiddingDebug('openInvitations:close-requested', {
                                view: biddingAssignmentsView,
                                method: 'close-button'
                            });
                            setBiddingAssignmentsView(null);
                        }} styles={roundedActionButtonStyles}>
                            Close
                        </DefaultButton>
                    </div>

                    {items.length === 0 ? (
                        <div
                            style={{
                                marginTop: 12,
                                border: '1px solid #e2e8f0',
                                borderRadius: 8,
                                padding: 12,
                                fontSize: 13,
                                color: '#6b7280'
                            }}
                        >
                            No {isOpenView ? 'open' : 'past'} invitations found in the current board range.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
            {items.map((item) => {
                const hasExpressedInterest = item.invitees.some((invitee) => invitee.status === 'expressed interest');
                const hasOpenedInvite = item.invitees.some((invitee) => invitee.status === 'opened');
                const hasViewedInvite = item.invitees.some((invitee) => invitee.status === 'viewed' || invitee.status === 'opened');
                const acceptedInvitee = item.invitees.find((invitee) => invitee.status === 'accepted');
                const statusLabel = !item.hasSelectedResource
                    ? (hasExpressedInterest
                        ? 'Expressed Interest'
                        : hasOpenedInvite
                            ? 'Opened'
                            : hasViewedInvite
                                ? 'Viewed'
                                : 'Invited')
                    : acceptedInvitee
                        ? 'Accepted'
                        : item.selectedFromInvitedPool
                            ? 'Accepted from invited pool'
                            : 'Auto-assigned';
                const autoAssignCountdownLabel = formatAutoAssignCountdown(item.autoAssignAt, invitationClockMs);
                const shiftStartCountdownLabel = formatShiftStartCountdown(item.start, invitationClockMs);
                const crmUrl = buildCrmUrl(getEnvironmentInfo(), 'msdyn_workorder', item.jobId);
                const replaceInvitedBadgeWithTimer = isOpenView
                    && !item.hasSelectedResource
                    && !hasExpressedInterest
                    && !hasOpenedInvite
                    && !hasViewedInvite;
                const badgeLabel = replaceInvitedBadgeWithTimer ? autoAssignCountdownLabel : statusLabel;
                const statusStyles = replaceInvitedBadgeWithTimer
                    ? { background: '#fff7ed', color: '#c2410c', border: '#fdba74' }
                    : !item.hasSelectedResource
                    ? hasExpressedInterest
                        ? { background: '#fff7ed', color: '#c2410c', border: '#fdba74' }
                        : hasOpenedInvite
                            ? { background: '#eef2ff', color: '#4338ca', border: '#c7d2fe' }
                            : hasViewedInvite
                                ? { background: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' }
                                : { background: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' }
                    : acceptedInvitee
                        ? { background: '#ecfdf3', color: '#166534', border: '#9ce6b4' }
                        : { background: '#fff7ed', color: '#c2410c', border: '#fdba74' };
                const getInviteeStatusStyles = (status: string) => {
                    switch (status) {
                        case 'opened':
                            return { background: '#eef2ff', color: '#4338ca', border: '#c7d2fe' };
                        case 'viewed':
                            return { background: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' };
                        case 'accepted':
                            return { background: '#ecfdf3', color: '#166534', border: '#9ce6b4' };
                        case 'declined':
                        case 'bid withdrawn':
                            return { background: '#fff1f2', color: '#9f1239', border: '#fecdd3' };
                        case 'expired':
                        case 'bid failed':
                            return { background: '#f3f4f6', color: '#374151', border: '#d1d5db' };
                        case 'invited':
                        case 'expressed interest':
                        default:
                            return { background: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' };
                    }
                };
                const getInviteeOutcomeStyles = (outcomeLabel?: string | null) => {
                    switch (outcomeLabel) {
                        case 'Chosen':
                        case 'Accepted':
                            return { background: '#ecfdf3', color: '#166534', border: '#9ce6b4' };
                        case 'Not chosen':
                        case 'Not accepted':
                            return { background: '#f3f4f6', color: '#374151', border: '#d1d5db' };
                        default:
                            return null;
                    }
                };

                return (
                    <div key={item.jobId} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <strong>{item.title}</strong>
                                <DefaultButton
                                    text={'Open Record'}
                                    styles={openInvitationsCrmButtonStyles}
                                    onClick={() => {
                                        logBiddingDebug('openInvitations:crm-link-clicked', {
                                            jobId: item.jobId,
                                            title: item.title,
                                            view: biddingAssignmentsView
                                        });
                                        window.open(crmUrl, '_blank', 'noopener,noreferrer');
                                    }}
                                />
                            </div>
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    padding: '3px 8px',
                                                    borderRadius: 999,
                                                background: statusStyles.background,
                                                color: statusStyles.color,
                                                border: `1px solid ${statusStyles.border}`
                                            }}
                                            >
                                                {badgeLabel}
                                            </span>
                        </div>
                        <div style={{ marginTop: 4, fontSize: 13, color: '#52606d' }}>
                            <span><strong>When:</strong> {asDateTimeLabel(item.start)} - {asDateTimeLabel(item.end)}</span>
                            {isOpenView && !replaceInvitedBadgeWithTimer && (
                                <span>  |  <strong>Auto-assign in:</strong> {autoAssignCountdownLabel}</span>
                            )}
                            <span>  |  <strong>Shift starts in:</strong> {shiftStartCountdownLabel}</span>
                            <span>  |  <strong>{item.hasSelectedResource ? (acceptedInvitee ? 'Accepted by:' : 'Auto-assigned to:') : 'Accepted by:'}</strong> {item.selectedResourceName ?? '-'}</span>
                            <span>  |  <strong>Invited resources:</strong> {item.invitationResourceCount}</span>
                        </div>
                        {item.invitees.length > 0 && (
                            <div style={{ marginTop: 8, borderTop: '1px solid #edf2f7', paddingTop: 8, display: 'grid', gap: 6 }}>
                                {item.invitees.map((invitee) => {
                                    const inviteeStatusStyles = getInviteeStatusStyles(invitee.status);
                                    const inviteeOutcomeStyles = getInviteeOutcomeStyles(invitee.outcomeLabel);
                                    const canChooseInvitee = isOpenView && !item.hasSelectedResource && invitee.status === 'expressed interest';
                                    const chooseDisabled = !!decisionLoadingByJob[item.jobId];
                                    return (
                                        <div
                                            key={`${item.jobId}_${invitee.resourceId}`}
                                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
                                        >
                                            <span style={{ fontSize: 13, color: '#1f2933' }}>{invitee.resourceName}</span>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        padding: '3px 8px',
                                                        borderRadius: 999,
                                                        background: inviteeStatusStyles.background,
                                                        color: inviteeStatusStyles.color,
                                                        border: `1px solid ${inviteeStatusStyles.border}`
                                                    }}
                                                >
                                                    {invitee.statusLabel}
                                                </span>
                                                {inviteeOutcomeStyles && invitee.outcomeLabel && (
                                                    <span
                                                        style={{
                                                            fontSize: 11,
                                                            fontWeight: 700,
                                                            padding: '3px 8px',
                                                            borderRadius: 999,
                                                            background: inviteeOutcomeStyles.background,
                                                            color: inviteeOutcomeStyles.color,
                                                            border: `1px solid ${inviteeOutcomeStyles.border}`
                                                        }}
                                                    >
                                                        {invitee.outcomeLabel}
                                                    </span>
                                                )}
                                                {canChooseInvitee && (
                                                    <PrimaryButton
                                                        styles={roundedActionButtonStyles}
                                                        onClick={() => void handleChooseInvitation(item, invitee)}
                                                        disabled={chooseDisabled}
                                                    >
                                                        {chooseDisabled ? <Spinner size={SpinnerSize.small} /> : 'Choose'}
                                                    </PrimaryButton>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    )}
                </div>
            </Modal>
        );
    }

    const handleSmartFindMatchesClick = async () => {
        if (smartFindLoading) return;
        setSmartFindLoading(true);
        setSmartFindProgress('Request sent...');
        lastAiStatusToastRef.current = '';
        closeToast();
        toast('info', 'Request sent...', { sticky: true, showCloseButton: false });
        let hasTerminalToast = false;

        const planningWindow = {
            start: startDate.toISOString(),
            end: endDate.toISOString()
        };

        const rangeStartMs = startDate.getTime();
        const rangeEndMs = endDate.getTime();
        const shiftsInRange = (getShiftsForCalendarView() ?? []).filter((shift) => {
            if (shift.display === 'unavailable') return false;
            const shiftStartMs = new Date(shift.start).getTime();
            const shiftEndMs = new Date(shift.end ?? shift.start).getTime();
            if (Number.isNaN(shiftStartMs)) return false;
            const effectiveEndMs = Number.isNaN(shiftEndMs) ? shiftStartMs : shiftEndMs;
            return shiftStartMs < rangeEndMs && effectiveEndMs > rangeStartMs;
        });

        const payload = buildAIEmpowerPayload({
            resources: getAllResources() ?? [],
            shifts: shiftsInRange,
            planningWindow,
            siteBaseLocation: siteLocationCoordinates,
            rsoConstraintLevels: activeLevels
        });
        const effectiveUnassignedJobs = (payload.unassignedJobs ?? []).filter((job: any) => {
            const jobId = String(job?.jobId ?? '');
            if (!jobId) return false;
            const shift = getShift(jobId);
            return !shift || !shift.resourceId || shift.resourceId === '0';
        });
        const effectivePayload: AIEmpowerPayload = {
            ...payload,
            unassignedJobs: effectiveUnassignedJobs
        };

        const assignedCount = shiftsInRange.filter((shift) => shift.resourceId && shift.resourceId !== '0').length;
        const unassignedCount = effectivePayload.unassignedJobs.length;

        try {
            if (typeof window !== "undefined") {
                (window as any).__AIEmpowerPayload = payload;
            }

            logBiddingDebug('smartFind:scope', {
                planningWindow,
                assignedCount,
                unassignedCount
            });
            logBiddingDebug('smartFind:payload', effectivePayload);

            if (effectivePayload.unassignedJobs.length === 0) {
                setCurrentBatch(null);
                setIsMatchModalOpen(false);
                closeToast();
                toast('info', 'No unassigned jobs in the current range.');
                hasTerminalToast = true;
                return;
            }

            const payloadUnassignedJobIds = (effectivePayload.unassignedJobs ?? [])
                .map((job: any) => String(job?.jobId ?? ''))
                .filter((jobId: string) => jobId.length > 0);
            clearSmartHighlightForJobs(payloadUnassignedJobIds);

            const aiResult = await streamChatGPTRequest(effectivePayload, (status) => {
                const cleanStatus = String(status ?? '').trim();
                if (!cleanStatus || lastAiStatusToastRef.current === cleanStatus) return;
                lastAiStatusToastRef.current = cleanStatus;
                setSmartFindProgress(cleanStatus);
                toast('info', cleanStatus, { sticky: true, showCloseButton: false });
                logBiddingDebug('smartFind:status', status);
            });

            logBiddingDebug('smartFind:allocations', aiResult);

            const suggestions = buildSmartSuggestions(
                effectivePayload,
                (Array.isArray(aiResult?.allocations) ? aiResult.allocations : []) as IAiAllocation[]
            );

            const batch: ISmartMatchBatch = {
                id: `${Date.now()}`,
                createdAt: new Date().toISOString(),
                planningWindow,
                items: buildBatchItems(effectivePayload, suggestions)
            };

            setCurrentBatch(batch);
            setIsMatchModalOpen(true);
            setDecisionLoadingByJob({});
            setExpandedResourceByJob({});

            const smartSuggestionPayload = {
                planningWindow,
                assignments: suggestions.assignments,
                unassigned: suggestions.unassigned,
                batch
            };

            if (typeof window !== "undefined") {
                (window as any).__AISmartAssignSuggestions = smartSuggestionPayload;
            }

            logBiddingDebug('smartFind:suggestions', smartSuggestionPayload);
            closeToast();
            toast('success', 'Smart matches are ready for review.');
            hasTerminalToast = true;
        } catch (error) {
            logBiddingDebug('smartFind:failed', { error: (error as any)?.message ?? String(error) });
            closeToast();
            toast('error', 'Smart Find Matches failed. Please try again.');
            hasTerminalToast = true;
        } finally {
            if (!hasTerminalToast) {
                closeToast();
            }
            setSmartFindProgress('Finding smart matches...')
            setSmartFindLoading(false);
            clearManualOverrideLevels();
            setSmartFindRunRanges((prev) => ({ ...prev, [currentSmartRangeKey]: true }));
        }
    }

    const renderMatchesMenuButton = () => {
        const menuProps: IContextualMenuProps = {
            items: [
                {
                    key: 'smart-find-matches',
                    text: hasSmartFindRun ? 'Rerun Smart Find Matches' : 'Smart Find Matches',
                    onClick: handleSmartFindMatchesClick,
                    disabled: smartFindLoading
                },
                {
                    key: 'review-matches',
                    text: 'Review Matches',
                    onClick: () => setIsMatchModalOpen(true),
                    disabled: smartFindLoading || !currentBatch
                },
                {
                    key: 'match-history',
                    text: 'Match History',
                    onClick: () => setIsHistoryModalOpen(true),
                    disabled: smartFindLoading
                }
            ]
        };

        return (
            <DefaultButton
                menuProps={menuProps}
                disabled={smartFindLoading}
                styles={compactToolbarButtonStyles}
                title={smartFindLoading ? smartFindProgress : undefined}
            >
                {smartFindLoading ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Spinner size={SpinnerSize.xSmall} />
                        Running...
                    </span>
                ) : 'Match Actions'}
            </DefaultButton>
        )
    }

    const renderValidateButton = () => {
        const menuProps: IContextualMenuProps = {
            items: [
                {
                    key: 'validate',
                    text: 'Validate',
                    onClick: validateAllShifts
                },
                {
                    key: 'publish',
                    text: 'Publish',
                    onClick: validateAndPublishShifts
                }
            ]
        }

        return <PrimaryButton menuProps={menuProps} disabled={smartFindLoading} styles={compactToolbarButtonStyles}>Validate</PrimaryButton>
    }

    const renderStatusChip = (status: TSmartDecision) => {
        const styles: Record<TSmartDecision, { label: string; color: string; background: string; border: string }> = {
            pending: { label: 'PENDING', color: '#1f2933', background: '#f8fafc', border: '#d9e2ec' },
            accepted: { label: 'ACCEPTED', color: '#05603a', background: '#ecfdf3', border: '#9ce6b4' },
            rejected: { label: 'REJECTED', color: '#9b1c1c', background: '#fdf2f2', border: '#f5c2c7' },
            unmatched: { label: 'UNMATCHED', color: '#6b7280', background: '#f8fafc', border: '#d1d5db' }
        };
        const chip = styles[status];
        return (
            <span
                style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    padding: '2px 8px',
                    borderRadius: 999,
                    color: chip.color,
                    background: chip.background,
                    border: `1px solid ${chip.border}`
                }}
            >
                {chip.label}
            </span>
        )
    }

    const renderMatchModal = () => {
        if (!currentBatch) return null;
        const pendingAssignableCount = currentBatch.items.filter((item) => item.status === 'pending' && !!item.suggestedResourceId).length;
        const blockClose = assignAllLoading;
        const handleDismiss = () => {
            if (blockClose) return;
            setIsMatchModalOpen(false);
        };

        return (
            <Modal isOpen={isMatchModalOpen} isBlocking={blockClose} onDismiss={handleDismiss}>
                <div style={{ width: 'min(1100px, 94vw)', maxHeight: '88vh', overflow: 'auto', padding: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <div>
                            <div style={{ fontSize: 24, fontWeight: 700 }}>Smart Match Suggestions</div>
                            <div style={{ fontSize: 12, color: '#52606d' }}>
                                Range: {asDateTimeLabel(currentBatch.planningWindow.start)} - {asDateTimeLabel(currentBatch.planningWindow.end)}
                            </div>
                        </div>
                        <div style={{ display: 'inline-flex', gap: 8 }}>
                            <DefaultButton
                                onClick={() => setIsHistoryModalOpen(true)}
                                disabled={assignAllLoading}
                                styles={roundedActionButtonStyles}
                            >
                                History
                            </DefaultButton>
                            <PrimaryButton onClick={handleAssignAll} disabled={assignAllLoading || smartFindLoading || pendingAssignableCount === 0} styles={roundedActionButtonStyles}>
                                {assignAllLoading ? 'Assigning...' : 'Assign To All'}
                            </PrimaryButton>
                            <DefaultButton onClick={handleDismiss} disabled={assignAllLoading} styles={roundedActionButtonStyles}>Close</DefaultButton>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                        {currentBatch.items.map((item) => {
                            const isBusy = decisionLoadingByJob[item.jobId] || assignAllLoading;
                            const isAccepted = item.status === 'accepted';
                            const canAssign = item.status === 'pending' && !!item.suggestedResourceId;
                            const canReject = item.status === 'pending' && !!item.suggestedResourceId;

                            return (
                                <div key={item.jobId} style={{ border: '1px solid #dfe3e8', borderRadius: 10, padding: 12, background: '#ffffff' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                            <strong>{item.title}</strong>
                                            {renderStatusChip(item.status)}
                                            {item.matchPercent != null && (
                                                <span style={{
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    color: '#0f766e',
                                                    background: '#e6fffa',
                                                    border: '1px solid #9ce9e2',
                                                    borderRadius: 999,
                                                    padding: '2px 8px'
                                                }}>
                                                    Matching score {item.matchPercent}
                                                </span>
                                            )}
                                        </div>
                                        {(canAssign || canReject || isAccepted) && (
                                            <div style={{ display: 'inline-flex', gap: 8 }}>
                                                {canAssign && (
                                                    <PrimaryButton
                                                        onClick={() => handleAssignOne(item)}
                                                        disabled={isBusy}
                                                        styles={roundedActionButtonStyles}
                                                    >
                                                        {isBusy ? 'Applying...' : 'Assign'}
                                                    </PrimaryButton>
                                                )}
                                                {canReject && (
                                                    <DefaultButton
                                                        onClick={() => handleRejectOne(item)}
                                                        disabled={isBusy}
                                                        styles={roundedActionButtonStyles}
                                                    >
                                                        Reject
                                                    </DefaultButton>
                                                )}
                                                {isAccepted && (
                                                    <DefaultButton disabled styles={assignedButtonStyles}>
                                                        Assigned
                                                    </DefaultButton>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ fontSize: 13, color: '#44546a', display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                                        <span><strong>Start:</strong> {asDateTimeLabel(item.start)}</span>
                                        <span><strong>End:</strong> {asDateTimeLabel(item.end)}</span>
                                        <span><strong>Duration:</strong> {item.durationLabel}</span>
                                    </div>

                                    <div style={{ marginTop: 10, borderTop: '1px dashed #e5e7eb', paddingTop: 10 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Recommended resource</div>
                                        {item.suggestedResourceId ? (
                                            <div
                                                role="button"
                                                onClick={() => toggleResourceDetails(item.jobId)}
                                                style={{
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: 8,
                                                    padding: 10,
                                                    background: '#f8fbff',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 600 }}>{item.suggestedResourceName}</div>
                                                    <div style={{ fontSize: 12, color: '#52606d', marginTop: 2 }}>
                                                        {item.reason || 'No reason provided.'}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: '#486581', marginTop: 6 }}>
                                                        {expandedResourceByJob[item.jobId] ? 'Hide details' : 'Click to view skills and fit details'}
                                                    </div>
                                                    {expandedResourceByJob[item.jobId] && (
                                                        <div style={{ marginTop: 10, borderTop: '1px dashed #dbe4f0', paddingTop: 10 }}>
                                                            <div style={{ fontSize: 12, color: '#334e68', marginBottom: 6 }}>
                                                                <strong>Skills</strong>
                                                            </div>
                                                            {(item.suggestedResourceDetails?.skills?.length ?? 0) > 0 ? (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                                    {(item.suggestedResourceDetails?.skills ?? []).slice(0, 12).map((skill) => (
                                                                        <span key={`${item.jobId}_${skill.label}`} style={{
                                                                            fontSize: 11,
                                                                            color: '#1e3a5f',
                                                                            background: '#e6f0ff',
                                                                            border: '1px solid #bfdbfe',
                                                                            borderRadius: 999,
                                                                            padding: '2px 8px'
                                                                        }}>
                                                                            {skill.label}
                                                                            {skill.expiryDate ? ` (exp ${skill.expiryDate})` : ''}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div style={{ fontSize: 12, color: '#6b7280' }}>
                                                                    No skills data was available for this resource.
                                                                </div>
                                                            )}

                                                            <div style={{ marginTop: 10, fontSize: 12, color: '#334e68' }}>
                                                                <strong>Categories:</strong> {(item.suggestedResourceDetails?.categories?.length ?? 0) > 0
                                                                    ? (item.suggestedResourceDetails?.categories ?? []).join(', ')
                                                                    : 'Not provided'}
                                                            </div>
                                                            <div style={{ marginTop: 4, fontSize: 12, color: '#334e68' }}>
                                                                <strong>Site locations:</strong> {(item.suggestedResourceDetails?.siteLocations?.length ?? 0) > 0
                                                                    ? (item.suggestedResourceDetails?.siteLocations ?? []).join(', ')
                                                                    : 'Not provided'}
                                                            </div>
                                                            <div style={{ marginTop: 4, fontSize: 12, color: '#334e68' }}>
                                                                <strong>Hours:</strong> Min payroll {item.suggestedResourceDetails?.minimumWorkHoursPerPayroll ?? '-'},
                                                                Max payroll {item.suggestedResourceDetails?.maximumWorkHourPerPayroll ?? '-'},
                                                                Max per shift {item.suggestedResourceDetails?.maxHoursPerShift ?? '-'}
                                                            </div>
                                                            {item.suggestedResourceDetails?.personalNote && (
                                                                <div style={{ marginTop: 6, fontSize: 12, color: '#4b5563', background: '#f8fafc', borderRadius: 6, padding: 8 }}>
                                                                    {item.suggestedResourceDetails.personalNote}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 12, color: '#486581', fontWeight: 600 }}>
                                                    {expandedResourceByJob[item.jobId] ? 'Hide' : 'Details'}
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{
                                                fontSize: 12,
                                                color: '#6b7280',
                                                border: '1px dashed #cbd5e1',
                                                borderRadius: 8,
                                                padding: 8,
                                                background: '#f8fafc'
                                            }}>
                                                No recommended resource could be safely assigned for this shift in the selected range. Please review and assign manually.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </Modal>
        )
    }

    const renderHistoryModal = () => {
        return (
            <Modal isOpen={isHistoryModalOpen} onDismiss={() => setIsHistoryModalOpen(false)}>
                <div style={{ width: 'min(900px, 92vw)', maxHeight: '85vh', overflow: 'auto', padding: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div>
                            <div style={{ fontSize: 22, fontWeight: 700 }}>Smart Match History</div>
                            <div style={{ fontSize: 12, color: '#52606d' }}>
                                Accepted and rejected actions from Smart Match review.
                            </div>
                        </div>
                        <DefaultButton onClick={() => setIsHistoryModalOpen(false)} styles={roundedActionButtonStyles}>Close</DefaultButton>
                    </div>

                    {smartMatchHistory.length === 0 ? (
                        <div style={{ border: '1px dashed #d1d5db', borderRadius: 8, padding: 10, color: '#6b7280' }}>
                            No history yet.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {smartMatchHistory.map((entry) => (
                                <div key={entry.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                            <strong>{entry.jobTitle}</strong>
                                            {renderStatusChip(entry.action === 'accepted' ? 'accepted' : 'rejected')}
                                        </div>
                                        <span style={{ fontSize: 12, color: '#6b7280' }}>{asDateTimeLabel(entry.timestamp)}</span>
                                    </div>
                                    <div style={{ marginTop: 4, fontSize: 13, color: '#52606d' }}>
                                        <span><strong>When:</strong> {asDateTimeLabel(entry.shiftStart)} - {asDateTimeLabel(entry.shiftEnd)}</span>
                                        {entry.resourceName && <span>  |  <strong>Resource:</strong> {entry.resourceName}</span>}
                                        {entry.matchPercent != null && <span>  |  <strong>Score:</strong> {entry.matchPercent}</span>}
                                    </div>
                                    {entry.reason && (
                                        <div style={{ marginTop: 6, fontSize: 12, color: '#4b5563', background: '#f8fafc', borderRadius: 6, padding: 8 }}>
                                            {entry.reason}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>
        )
    }

    return (
        <>
            <div style={{ display: "inline-flex", gap: '5px', alignItems: 'center' }}>
                <DefaultButton
                    onClick={openRsoConfiguration}
                    disabled={smartFindLoading}
                    styles={compactToolbarButtonStyles}
                    title={`Current RSO source: ${activePresetLabel}`}
                >
                    Configure RSO
                </DefaultButton>
                {renderBiddingActionsButton()}
                {renderMatchesMenuButton()}
                {renderDraftButton()}
                {renderValidateButton()}
                <DefaultButton onClick={undoShiftMove} title="Undo" disabled={smartFindLoading} styles={compactToolbarIconButtonStyles}>
                    <FontAwesomeIcon icon={["fas", "rotate-left" as IconName]} />
                </DefaultButton>
                <DefaultButton onClick={reloadShifts} title="Reload" disabled={smartFindLoading} styles={compactToolbarIconButtonStyles}>
                    <FontAwesomeIcon icon={["fas", "arrows-rotate" as IconName]} />
                </DefaultButton>
            </div>
            {renderBiddingAssignmentsModal()}
            {renderMatchModal()}
            {renderHistoryModal()}
            {smartFindLoading && (
                <div
                    aria-hidden="true"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(15, 23, 42, 0.16)',
                        zIndex: 1040,
                        cursor: 'progress'
                    }}
                />
            )}
        </>
    )
}

export { DraftControl }


